import { gql } from 'graphql-request';
import { createMemo, For, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { Grid } from '../../grid-system/grid/grid';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { ErrorCatcher } from '../../tools/error-catcher';
import { gridSettings, SidebarContainerLine } from '../../ui-components/layouts/sidebar.style';
import { Slider } from '../../ui-components/slider/slider';
import { ProductContainer, ProductsData } from '../products/products-types';
import { ProductsSpotlightProps } from './products-spotlight.d';
import { createGroupedProductCard } from '../products/products';
import clsx from 'clsx';

const GET_PRODUCTS_QUERY = gql`
    query GetProducts(
        $tags: [String]
    ) {
        products: resources(
            type: ["product"]
            tags: $tags
        ) {
            content
        }
    }
`;

export const ProductsSpotlight = (props: ProductsSpotlightProps) => {
    const { createCachedResource, viewport } = useContext(AppContext);
    const [productsContent] = createCachedResource(GET_PRODUCTS_QUERY, () => ({ tags: [props?.tag?.slug] }), true);
    const productsData = createMemo((): ProductsData => productsContent()?.products);

    const isTablet = () => viewport.width <= theme.breakpoints.TABLET;

    const createSliderCards = () => {
        let productsPerView = 3;
        let gapSize = theme.grid.standardUnit;

        switch (true) {
            case viewport.width <= theme.breakpoints.DESKTOP_LARGE && viewport.width > theme.breakpoints.TABLET:
                gapSize = theme.grid.gapSizeTablet;
                productsPerView = 2;
                break;
            case viewport.width <= theme.breakpoints.TABLET:
                productsPerView = 1;
                break;
        }

        const sliderGroups: ProductContainer[][] = [];

        const customSlidesProductSlugs: string[] | [] = props.customSlides && Object.values(props.customSlides).filter((slide: string) => slide !== '');

        const customSort = (a: ProductContainer, b: ProductContainer) => {
            if(!customSlidesProductSlugs.length){
                return 0;
            }

            const slugA = a.content.slug;
            const slugB = b.content.slug;

            if (slugA === slugB) return 0;

            const indexA = customSlidesProductSlugs.indexOf(slugA);
            const indexB = customSlidesProductSlugs.indexOf(slugB);

            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
        };


        let count = 0;
        productsData()
            .map((prod: ProductContainer) => prod)
            .sort((a: ProductContainer, b: ProductContainer) => {
                return a.content.post_title.localeCompare(b.content.post_title);
            })
            .sort(customSort)
            .forEach((product: ProductContainer, index: number) => {
                if (!sliderGroups[count]) sliderGroups[count] = [];

                sliderGroups[count].push(product);

                const productNumber = index + 1;
                if (productNumber % productsPerView === 0) {
                    count++;
                }
            });

        return (
            <Slider slidesPerView={1} spacing={gapSize} slideHeight={'auto'}>
                <For each={sliderGroups}>
                    {(group: ProductContainer[]) => (
                        <Grid {...gridSettings.listing} customCss={'height: auto;'}>
                            <For each={group}>{(product: ProductContainer) => {
                                return createGroupedProductCard(product.content);
                            }}</For>
                        </Grid>
                    )}
                </For>
            </Slider>
        );
    };

    return (
        <ErrorCatcher componentName="Products spotlight">
            <Show when={productsData()}>
                <Section
                    templateShorthand={[12]}
                    widthType={'bgFull'}
                    backgroundType={'color'}
                    backgroundValue={props.backgroundColor?.name || 'white'}
                    removeSpacingBlock={true}
                    customCss={'padding-top: 0 !important; padding-bottom: 0 !important;'}
                    {...(props.sectionIdentifier
                        ? {
                            sectionIdentifier: props.sectionIdentifier,
                        }
                        : {})}
                >
                    <Grid {...gridSettings.container}>
                        <div class="h-full max-tablet:pr-0 max-tablet:pt-12">
                            <SidebarContainerLine>
                                <div class="flex flex-col justify-between h-full mr-12 max-tablet:static max-tablet:px-0 max-tablet:mr-0 max-tablet:pb-0 max-tablet:z-[2]">
                                    <div class="pt-24 max-tablet:pt-4 max-tablet:px-6">
                                        <DynamicModuleGenerator skipSectionConversion={true} content={[props?.mainHeading]} />
                                        <DynamicModuleGenerator skipSectionConversion={true} content={[props?.subHeading]} />
                                    </div>

                                    <Show when={!isTablet()}>
                                        <div class="mt-auto mb-44">
                                            <DynamicModuleGenerator skipSectionConversion={true} content={[props?.button]} />
                                        </div>
                                    </Show>
                                </div>
                            </SidebarContainerLine>
                        </div>

                        <div class="pt-20 pb-8 max-tablet:pt-0 max-tablet:pb-0">
                            <div>{createSliderCards()}</div>
                        </div>

                        <Show when={isTablet()}>
                            <div class={clsx(
                                isTablet() ? 'mt-[-1.5rem] mb-12' : 'mt-auto mb-44'
                            )}>
                                <DynamicModuleGenerator skipSectionConversion={true} content={[props?.button]} />
                            </div>
                        </Show>
                    </Grid>
                </Section>
            </Show>
        </ErrorCatcher>
    );
};

ProductsSpotlight.parseProps = (atts: any) => {
    return {
        ...atts,
        mainHeading: atts.innerBlocks[0],
        subHeading: atts.innerBlocks[1],
        button: atts.innerBlocks[2],
        tag: atts.tag,
        backgroundColor: atts.backgroundColor,
        sectionIdentifier: atts.sectionIdentifier,
        customSlides: atts.customSlides || [],
    };
};
